import {
  Button,
  Card,
  Chip,
  type ChipProps,
  ClickAwayListener,
  Popper,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { cn, styleVars } from 'src/utils/styles';
import { P, match } from 'ts-pattern';
import SkillIconsGithubDark from '~icons/skill-icons/github-dark';
import TablerIconChevronLeft from '~icons/tabler/chevron-left';
import TablerIconPlus from '~icons/tabler/plus';
import { GitHubFileIcon } from '#/features/github';
import ChatContextMenu from './chat-context-menu';
import ChatContextMenuCode from './chat-context-menu-code';
import type { ChatContextOptionId } from './chat-context-options';
import {
  getChatContextGitHubRepositoryFiles,
  isChatContextGitHubRepository,
  isChatContextGitHubRepositoryFile,
} from './context-data';
import { getChatContextGitHubRepositories } from './context-data';
import type {
  ChatContextGitHubRepository,
  ChatContextGitHubRepositoryFile,
  ChatContextItem,
  OnChangeChatContextCallback,
} from './types';

export interface ChatContextProps {
  /**
   * Whether the context menu is disabled.
   */
  disabled?: boolean;
  /**
   * The context items to display.
   */
  context: ChatContextItem[];
  /**
   * The callback to call when the context changes.
   */
  onContextChange?: OnChangeChatContextCallback;
}

type ActiveOption = ChatContextOptionId | 'menu';

function ChatContext({
  disabled = false,
  context,
  onContextChange,
}: ChatContextProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [popperAnchor, setPopperAnchor] =
    React.useState<HTMLButtonElement | null>(null);
  const [activeOption, setActiveOption] = React.useState<ActiveOption>('menu');
  const theme = useTheme();

  /*
  |------------------
  | Computed
  |------------------
  */

  const contextRepositories = React.useMemo(() => {
    return getChatContextGitHubRepositories(context);
  }, [context]);
  const contextFiles = React.useMemo(() => {
    return getChatContextGitHubRepositoryFiles(context);
  }, [context]);

  /*
  |------------------
  | Callbacks
  |------------------
  */

  function createOnDelete(item: ChatContextItem) {
    return match(onContextChange)
      .with(P.nullish, () => undefined)
      .otherwise(() => () => {
        handleRemoveContext(item);
      });
  }

  const handleOpen = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleChangeContext = React.useCallback(
    (item: ChatContextItem[]) => {
      if (!onContextChange) {
        return;
      }

      const files = getChatContextGitHubRepositoryFiles(item);
      const repos = getChatContextGitHubRepositories(item);
      onContextChange([...files, ...repos]);
    },
    [onContextChange]
  );

  const handleRemoveContext = React.useCallback(
    (item: ChatContextItem) => {
      if (!onContextChange) {
        return;
      }

      onContextChange(
        context.filter((c) => {
          if (
            isChatContextGitHubRepositoryFile(c) &&
            isChatContextGitHubRepositoryFile(item)
          ) {
            return c.path !== item.path;
          }

          if (
            isChatContextGitHubRepository(c) &&
            isChatContextGitHubRepository(item)
          ) {
            return c.id !== item.id;
          }

          return true;
        })
      );
    },
    [context, onContextChange]
  );

  return (
    <div id='chat-context' className='relative'>
      <div className='flex flex-row items-center gap-1'>
        {onContextChange && (
          <Tooltip
            title='Add context to your chat session to help the AI understand your request.'
            placement='left'
            enterDelay={1000}
            enterNextDelay={1000}
            arrow
          >
            <span ref={ref}>
              <ChatContextButton
                disabled={disabled}
                showLabel={
                  contextFiles.length === 0 && contextRepositories.length === 0
                }
                onClick={handleOpen}
              />
            </span>
          </Tooltip>
        )}
        <div className='flex flex-row gap-1 flex-wrap'>
          {contextRepositories.map((item) => (
            <ChatContextRepositoryChip
              {...item}
              key={item.id}
              disableDelete={contextFiles.some(
                (file) =>
                  file.githubRepository.id.toString() === item.id.toString()
              )}
              onDelete={createOnDelete(item)}
            />
          ))}
          {match(contextFiles)
            .when(
              (x) => x.length > 5,
              (x) => {
                const [first, second, ...rest] = x;
                return (
                  <React.Fragment>
                    {[first, second].map((item) => (
                      <ChatContextCodeFileChip
                        {...item}
                        key={item.path}
                        onDelete={createOnDelete(item)}
                      />
                    ))}
                    <Popper
                      placement='top'
                      open={!!popperAnchor}
                      anchorEl={popperAnchor}
                      slotProps={{
                        root: {
                          className: 'z-[var(--chat-context-overflow-z)]',
                          style: styleVars({
                            '--chat-context-overflow-z': (
                              theme.zIndex.appBar + 1
                            ).toString(),
                          }),
                        },
                      }}
                    >
                      <ClickAwayListener
                        onClickAway={() => setPopperAnchor(null)}
                      >
                        <div className='p-2 mb-2 rounded-sm bg-background-paper w-auto min-w-[300px] max-w-[500px] border border-divider '>
                          <Stack
                            direction='row'
                            spacing={1}
                            useFlexGap
                            sx={{ flexWrap: 'wrap' }}
                          >
                            {rest.map((item) => (
                              <ChatContextCodeFileChip
                                {...item}
                                key={item.path}
                                onDelete={createOnDelete(item)}
                              />
                            ))}
                          </Stack>
                        </div>
                      </ClickAwayListener>
                    </Popper>
                    <Button
                      size='small'
                      variant='text'
                      className='min-w-0 px-2 border border-divider min-h-auto leading-none'
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setPopperAnchor(e.currentTarget);
                      }}
                    >
                      +{rest.length}
                    </Button>
                  </React.Fragment>
                );
              }
            )
            .otherwise(() =>
              contextFiles.map((item) => (
                <ChatContextCodeFileChip
                  {...item}
                  key={item.path}
                  onDelete={createOnDelete(item)}
                />
              ))
            )}
        </div>
      </div>
      {onContextChange && (
        <Popper
          id='chat-context-menu'
          placement='bottom-start'
          anchorEl={ref.current}
          open={open}
          className='min-w-[300px] min-h-[200px] mt-1 z-(--chat-context-menu-z)'
          style={styleVars({
            '--chat-context-menu-z': theme.zIndex.modal.toString(),
          })}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Card className='pt-2 pb-2'>
              {match(activeOption)
                .with('menu', () => (
                  <ChatContextMenu onChangeActiveOption={setActiveOption} />
                ))
                .with('code', () => (
                  <ChatContextMenuCode
                    context={[...contextFiles, ...contextRepositories]}
                    onChangeContext={handleChangeContext}
                  />
                ))
                .otherwise(() => null)}

              {activeOption !== 'menu' && (
                <div className='px-2 py-1 w-full'>
                  <Button
                    size='small'
                    fullWidth
                    variant='text'
                    startIcon={<TablerIconChevronLeft />}
                    onClick={() => setActiveOption('menu')}
                  >
                    Go back
                  </Button>
                </div>
              )}
            </Card>
          </ClickAwayListener>
        </Popper>
      )}
    </div>
  );
}

export default ChatContext;

/*
|------------------
| Components
|------------------
*/

export interface ChatContextCodeFileChipProps
  extends ChatContextGitHubRepositoryFile,
    Omit<ChipProps, 'label' | 'icon'> {}

function ChatContextCodeFileChip({
  path,
  name,
  githubRepository,
  onDelete,
  ...props
}: ChatContextCodeFileChipProps) {
  return (
    <Tooltip
      key={path}
      title={`@${githubRepository.owner.login}/${githubRepository.name}/${path}`}
      placement='top'
      arrow
    >
      <Chip
        {...props}
        icon={
          <GitHubFileIcon
            fileExtension={name.split('.').pop() ?? ''}
            iconType='brand'
          />
        }
        size='medium'
        variant='outlined'
        shape='rounded'
        label={name}
        onDelete={onDelete}
      />
    </Tooltip>
  );
}

interface ChatContextRepositoryChipProps
  extends ChatContextGitHubRepository,
    Omit<ChipProps, 'label' | 'icon' | 'id'> {
  /**
   * Whether to disable the delete icon.
   */
  disableDelete?: boolean;
}

function ChatContextRepositoryChip({
  name,
  owner,
  id,
  disableDelete = false,
  disabled = false,
  onDelete,
  ...props
}: ChatContextRepositoryChipProps) {
  return (
    <Tooltip
      title={match(disableDelete)
        .with(
          true,
          () =>
            'This cannot be removed as there are files belonging to this repository in the context'
        )
        .with(false, () => `@${owner.login}/${name}`)
        .exhaustive()}
      placement='top'
      arrow
    >
      <span>
        <Chip
          {...props}
          disabled={disabled || disableDelete}
          variant='outlined'
          shape='rounded'
          icon={<SkillIconsGithubDark />}
          label={name}
          onDelete={onDelete}
        />
      </span>
    </Tooltip>
  );
}

interface ChatContextButtonProps
  extends React.HTMLAttributes<HTMLButtonElement> {
  /**
   * Whether the button is disabled.
   */
  disabled?: boolean;
  /**
   * Whether to show the label.
   */
  showLabel?: boolean;
}

function ChatContextButton({
  disabled,
  showLabel = true,
  className,
  ...props
}: ChatContextButtonProps) {
  return (
    <button
      {...props}
      id='chat-context-button'
      type='button'
      disabled={disabled}
      className={cn(
        'cursor-pointer bg-transparent rounded-sm',
        'text-primary-contrast',
        'border border-divider',
        !disabled && 'hover:border-primary-contrast',
        disabled && 'opacity-50',
        className
      )}
    >
      <div className='flex flex-row items-center'>
        <div className={cn('pl-1 pr-1 flex items-center justify-center')}>
          <TablerIconPlus className='size-4' />
        </div>
        <div
          className={cn({
            'pr-2 py-[8px]': showLabel,
            'w-0 overflow-hidden': !showLabel,
          })}
        >
          Add Context
        </div>
      </div>
    </button>
  );
}
